import { useRef, useState } from "react";
import { api } from "../api";
import { canUseGateway, recipientDetails } from "../announcementPresentation";
import type { BootstrapResponse } from "../types";
import { AnnouncementGatewayStatus } from "./AnnouncementGatewayStatus";
import type { AnnouncementPreview } from "./AnnouncementSendDialog";
import { useAnnouncementConfig } from "./useAnnouncementConfig";
import { useDraftLeaveGuard } from "./useDraftLeaveGuard";
import { Button } from "./ui/Button";
import { Textarea } from "./ui/Field";
import { Modal } from "./ui/Modal";
import { ChevronLeft } from "lucide-react";
import { AligoSettingsPage } from "./AligoSettingsPage";
import { confirmAction } from "../confirmAction";
import { Feedback } from "./ui/Surface";
import "./AnnouncementMessages.css";

type SendResult = { error: boolean; text: string; failures: Array<{ workerId: string; workerName: string; message: string }> };

/** onExit: 창 안에서 열렸을 때, 그 창째로 떠나는 길. 주면 ✕ 는 통째로 닫고 ‹ 는 한 걸음만
 *  되돌아간다 — 두 뎁스에서는 닫는 손잡이가 둘이라 어느 쪽이 화면을 떠나는지 흐려진다. */
export function AnnouncementMessages({ data, initialRecipientIds, onClose, onExit }: { data: BootstrapResponse; initialRecipientIds: readonly string[]; onClose: () => void; onExit?: () => void }) {
  const config = useAnnouncementConfig();
  /* 받는 사람을 창 안에서 뺄 수 있다. 예전에는 밖에서 고른 그대로 굳어 있어서, 한 명을
     잘못 골랐으면 창을 닫고 표로 돌아가 다시 골라야 했다 — 더하기는 여전히 밖의 일이다. */
  const [recipientIds, setRecipientIds] = useState<readonly string[]>(initialRecipientIds);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false), [sendError, setSendError] = useState(""), [result, setResult] = useState<SendResult | null>(null);
  const [acknowledged, setAcknowledged] = useState("");
  const sender = useRef(false);
  const recipients = recipientDetails(recipientIds, data.workers);
  const valid = recipients.filter(member => member.deliverable).length, signature = JSON.stringify({ recipientIds, message });
  /* 2026-10-19 밤 지윤: 나갈 때 되묻지 않는다 — 공지는 몇 줄짜리라 다시 쓰는 비용이 확인 창을 읽는 비용보다 작다.
     보내는 중에만 막는다(나가면 보냈는지 모르게 된다). */
  useDraftLeaveGuard({ dirty: false, busy: sending });
  const canSend = !sending && canUseGateway(config.value) && !!message.trim() && message.length <= 5000 && valid > 0 && recipientIds.length <= 300;
  const canTest = !sending && canUseGateway(config.value) && !!config.value?.testReceiverConfigured && !!message.trim() && message.length <= 5000;
  /* 미리 보는 창을 한 겹 더 띄우지 않는다 — 그 창에 적히던 것(받는 사람 수·테스트 모드
     여부·본문)은 전부 이 창에 이미 서 있어, 같은 말을 한 번 더 읽고 한 번 더 누르는 일이었다.
     되돌릴 수 없는 일이라 묻긴 물어야 하므로, 기기의 되묻는 창을 쓴다 — 삭제와 같은 규칙이다. */
  const openSend = (kind: "group" | "test") => {
    if (kind === "group" ? !canSend : !canTest) return;
    const testMode = config.value!.testMode;
    const who = kind === "test" ? "설정된 테스트 번호" : `${valid}명`;
    const what = testMode ? "테스트 모드라 실제 문자는 나가지 않습니다." : "실제 문자가 나갑니다. 보낸 뒤에는 되돌릴 수 없습니다.";
    if (!confirmAction(`${who}에게 보내시겠습니까?`, what)) return;
    setSendError("");
    void send({ kind, message, recipients, testMode });
  };
  const send = async (attempted: AnnouncementPreview) => {
    if (sender.current) return;
    const draftSignature = signature;
    sender.current = true; setSending(true); setSendError(""); setResult(null);
    try {
      if (attempted.kind === "test") {
        await api.sendAnnouncementTestMessage(attempted.message);
        setResult({ error: false, text: attempted.testMode ? "테스트 모드 전송 요청을 확인했습니다. 실제 문자는 발송되지 않았습니다." : "설정된 테스트 번호로 발송했습니다.", failures: [] });
      } else {
        const response = await api.sendAnnouncementMessage({ workerIds: attempted.recipients.filter(member => member.exists).map(member => member.id), message: attempted.message });
        const skipped = response.skipped + attempted.recipients.filter(member => !member.exists).length;
        setResult({ error: response.failed > 0, text: `발송 완료 · 성공 ${response.sent}건 · 실패 ${response.failed}건 · 제외 ${skipped}건${attempted.testMode ? " · 테스트 모드" : ""}`, failures: response.failures });
        if (!response.failed) setAcknowledged(draftSignature);
      }
    } catch (error) {
      const text = `${error instanceof Error ? error.message : "전송 결과를 확인하지 못했습니다."} 중복 발송을 피하려면 알리고 발송 내역을 확인한 뒤 다시 진행해 주세요.`;
      setSendError(text); setResult({ error: true, text, failures: [] });
    } finally { sender.current = false; setSending(false); }
  };
  const requestClose = () => {
    if (sending) return;
    onClose();
  };
  return <Modal onBack={onExit ? requestClose : undefined} title={<>공지 메시지 작성 <span className="message-composer-recipient-count">받는 사람 {valid}명</span></>} onClose={() => { requestClose(); onExit?.(); }} busy={sending} className="message-composer-modal" bodyClassName="message-composer">
    {config.error && <Feedback tone="error">{config.error}<Button size="compact" onClick={() => void config.reload()}>설정 다시 확인</Button></Feedback>}
    {config.value && !canUseGateway(config.value) && <Feedback>계정과 발신번호 설정을 확인해 주세요. 메세지 작성은 사용할 수 있습니다.</Feedback>}
    {recipientIds.length > 300 && <Feedback tone="error">한 번에 최대 300명까지 선택할 수 있습니다. 수신자를 줄여 주세요.</Feedback>}
    {/* 2026-10-19 밤 지윤: 한 단계 들어간 다른 창(작업자 등록·수동 정산)과 같은 설정 줄 문법 — 제목 줄(‹ + 15/20 580),
        줄마다 왼쪽 이름 · 오른쪽 내용, 메시지처럼 넓은 칸은 이름 아래로 쌓는다. */}
    <div className="worker-profile-page-title">
      {onExit && <button type="button" className="worker-profile-page-back" aria-label="뒤로" disabled={sending} onClick={requestClose}><ChevronLeft size={20} aria-hidden="true" /></button>}
      <span aria-hidden="true">공지 메시지 작성</span>
    </div>
    <div className="payroll-form-grid message-form-grid">
      {/* 이름을 늘어놓는다. "받는 사람 N명"만 있을 때는 그 N이 누구인지 확인할 길이 없었는데,
          한 번 보내면 되돌릴 수 없는 일이라 보낼 사람이 보여야 한다. */}
      <div className="ui-field message-recipients-row" role="group" aria-labelledby="message-recipients-label">
        <label id="message-recipients-label">받는 사람 <span className="ui-field-optional message-composer-recipient-count" aria-live="polite">{valid}명</span></label>
        {recipients.length > 0 ? <ul className="message-recipient-chips">
          {recipients.map(member => <li key={member.id} data-deliverable={member.deliverable || undefined}>
            {member.name}
            <button type="button" aria-label={`${member.name} 제외`} disabled={sending}
              onClick={() => setRecipientIds(current => current.filter(id => id !== member.id))}>✕</button>
          </li>)}
        </ul> : <span className="message-recipients-empty">–</span>}
      </div>
      <div className="ui-field message-stack-row">
        <label htmlFor="announcement-message">메시지</label>
        <Textarea id="announcement-message" aria-label="공지메시지" disabled={sending} maxLength={5000} value={message} onChange={event => setMessage(event.target.value)} placeholder="전송할 메시지를 입력하세요." />
      </div>
      <div className="ui-field message-stack-row message-status-row">
        <label>발송 상태</label>
        <AnnouncementGatewayStatus config={config} sending={sending} />
      </div>
      {/* 발신 설정은 이 창 안에 그대로 꺼내 둔다 — 보내려다 설정이 비어 있는 걸 알게 되는
          자리가 여기이고, 창을 한 겹 더 띄우면 그 창이 이미 이 창에 있는 말(발송 상태·테스트
          문자)을 한 번 더 늘어놓는다. 같은 칸을 빌려 쓰므로 두 벌이 되지 않는다. */}
      <details className="ui-field message-gateway-settings">
        <summary>발신 설정</summary>
        <AligoSettingsPage only="credentials" config={config} />
      </details>
    </div>
    <div className="message-send-footer"><span>{message.length.toLocaleString()} / 5,000자</span><Button disabled={!canTest} onClick={() => openSend("test")}>테스트 문자 발송</Button><Button variant="primary" disabled={!canSend} busy={sending} onClick={() => openSend("group")}>보내기</Button></div>
    {result && <Feedback tone={result.error ? "error" : "success"}>{result.text}{result.failures.length > 0 && <details className="message-failures"><summary>실패한 수신자 {result.failures.length}명 확인</summary><ul>{result.failures.map(item => <li key={item.workerId}><strong>{item.workerName}</strong><p>{item.message}</p></li>)}</ul></details>}</Feedback>}

  </Modal>;
}
